/**
 * `ViewEvent`: a redacted engine event plus its log line (engine `eventsFor`, docs/design/05 §5). Events
 * drive animations and the log; hidden parts arrive as `null` (`{ id: null, card: null }` = "a card").
 */
import { z } from 'zod';
import {
  AudienceSchema,
  BattlefieldIdSchema,
  CardRefSchema,
  ChainItemIdSchema,
  DecisionKindSchema,
  DeckZoneKindSchema,
  ObjectIdSchema,
  PhaseSchema,
  PlayerIdSchema,
  TierSchema,
  TokenIdSchema,
  ZoneRefSchema,
  int,
  text,
} from './primitives';
import { SpellChangeSchema } from './commands';
import { PromptSchema } from './decisions';
import { ChainItemKindSchema, GameResultSchema } from './view';

export const ViewObjRefSchema = z.strictObject({ id: ObjectIdSchema.nullable(), card: CardRefSchema.nullable() });

export const LogPartSchema = z.discriminatedUnion('k', [
  z.strictObject({ k: z.literal('text'), text: z.string().max(500) }),
  z.strictObject({ k: z.literal('player'), player: PlayerIdSchema }),
  z.strictObject({ k: z.literal('card'), ref: ViewObjRefSchema }),
  z.strictObject({ k: z.literal('zone'), zone: ZoneRefSchema }),
  z.strictObject({ k: z.literal('battlefield'), battlefield: BattlefieldIdSchema }),
  z.strictObject({ k: z.literal('token'), tokenId: TokenIdSchema }),
  z.strictObject({ k: z.literal('number'), n: z.number() }),
]);

export const LogLineSchema = z.strictObject({
  actor: PlayerIdSchema.nullable(),
  tone: z.enum(['normal', 'loud', 'system']),
  parts: z.array(LogPartSchema),
});

const ref = ViewObjRefSchema;
const actor = PlayerIdSchema.nullable();
const n = int(-1_000_000, 1_000_000);
const log = LogLineSchema;
const position = z.union([z.literal('top'), z.literal('bottom'), int(0, 10_000), z.null()]);

export const ViewEventSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('gameCreated'),
    actor: z.null(),
    matchId: text(128),
    tier: TierSchema,
    mode: z.literal('duel'),
    seats: z.array(PlayerIdSchema),
    commitment: z.string(),
    log,
  }),
  z.strictObject({
    type: z.literal('turnOrderDetermined'),
    actor: z.null(),
    order: z.array(PlayerIdSchema),
    firstPlayer: PlayerIdSchema,
    log,
  }),
  z.strictObject({
    type: z.literal('legendPlaced'),
    actor: z.null(),
    player: PlayerIdSchema,
    legend: ref,
    champion: ref.nullable(),
    log,
  }),
  z.strictObject({
    type: z.literal('battlefieldsPlaced'),
    actor: z.null(),
    battlefields: z.array(z.strictObject({ battlefield: BattlefieldIdSchema, card: ref, owner: PlayerIdSchema })),
    log,
  }),
  z.strictObject({ type: z.literal('battlefieldChosen'), actor: PlayerIdSchema, player: PlayerIdSchema, log }),
  z.strictObject({
    type: z.literal('shuffled'),
    actor,
    player: PlayerIdSchema,
    zone: DeckZoneKindSchema,
    count: n,
    log,
  }),
  z.strictObject({ type: z.literal('drew'), actor, player: PlayerIdSchema, cards: z.array(ref), requested: n, log }),
  z.strictObject({
    type: z.literal('channeled'),
    actor,
    player: PlayerIdSchema,
    runes: z.array(ref),
    requested: n,
    log,
  }),
  z.strictObject({
    type: z.literal('mulliganed'),
    actor: PlayerIdSchema,
    player: PlayerIdSchema,
    setAside: z.array(ref),
    drawn: z.array(ref),
    log,
  }),
  z.strictObject({
    type: z.literal('moved'),
    actor,
    from: ZoneRefSchema,
    to: ZoneRefSchema,
    object: ref,
    result: ref.nullable(),
    position,
    log,
  }),
  z.strictObject({
    type: z.literal('looked'),
    actor: PlayerIdSchema,
    player: PlayerIdSchema,
    zone: DeckZoneKindSchema,
    cards: z.array(ref),
    log,
  }),
  z.strictObject({ type: z.literal('revealed'), actor: PlayerIdSchema, cards: z.array(ref), to: AudienceSchema, log }),
  z.strictObject({ type: z.literal('exhausted'), actor, objects: z.array(ref), log }),
  z.strictObject({ type: z.literal('readied'), actor, objects: z.array(ref), log }),
  z.strictObject({ type: z.literal('counterChanged'), actor, object: ref, kind: text(32), delta: n, value: n, log }),
  z.strictObject({ type: z.literal('statusChanged'), actor, object: ref, status: text(32), on: z.boolean(), log }),
  z.strictObject({ type: z.literal('attached'), actor, object: ref, to: ref, log }),
  z.strictObject({ type: z.literal('detached'), actor, object: ref, from: ref, log }),
  z.strictObject({
    type: z.literal('tokensCreated'),
    actor,
    tokenId: TokenIdSchema,
    objects: z.array(ref),
    to: ZoneRefSchema,
    log,
  }),
  z.strictObject({ type: z.literal('battlefieldAdded'), actor, battlefield: BattlefieldIdSchema, card: ref, log }),
  z.strictObject({ type: z.literal('pointsChanged'), actor, player: PlayerIdSchema, delta: n, total: n, log }),
  z.strictObject({ type: z.literal('xpChanged'), actor, player: PlayerIdSchema, delta: n, total: n, log }),
  z.strictObject({
    type: z.literal('battlefieldControlChanged'),
    actor,
    battlefield: BattlefieldIdSchema,
    from: PlayerIdSchema.nullable(),
    to: PlayerIdSchema.nullable(),
    log,
  }),
  z.strictObject({
    type: z.literal('controlGiven'),
    actor: PlayerIdSchema,
    object: ref,
    from: PlayerIdSchema,
    to: PlayerIdSchema,
    log,
  }),
  z.strictObject({
    type: z.literal('stacked'),
    actor: PlayerIdSchema,
    item: ChainItemIdSchema,
    kind: ChainItemKindSchema,
    source: ref,
    log,
  }),
  z.strictObject({
    type: z.literal('unstacked'),
    actor: PlayerIdSchema,
    item: ChainItemIdSchema,
    kind: ChainItemKindSchema,
    source: ref.nullable(),
    log,
  }),
  z.strictObject({ type: z.literal('phaseChanged'), actor, from: PhaseSchema, to: PhaseSchema, log }),
  z.strictObject({ type: z.literal('turnEnded'), actor, player: PlayerIdSchema, turn: n, log }),
  z.strictObject({ type: z.literal('turnStarted'), actor: z.null(), player: PlayerIdSchema, turn: n, log }),
  z.strictObject({ type: z.literal('priorityPassed'), actor: PlayerIdSchema, to: PlayerIdSchema.nullable(), log }),
  z.strictObject({
    type: z.literal('targeted'),
    actor: PlayerIdSchema,
    item: ChainItemIdSchema,
    source: ref.nullable(),
    targets: z.array(ref),
    log,
  }),
  z.strictObject({
    type: z.literal('modified'),
    actor: PlayerIdSchema,
    item: ChainItemIdSchema,
    source: ref.nullable(),
    target: ref,
    change: SpellChangeSchema,
    log,
  }),
  z.strictObject({
    type: z.literal('pointed'),
    actor: PlayerIdSchema,
    from: ref,
    to: z.union([z.strictObject({ object: ref }), z.strictObject({ zone: ZoneRefSchema })]),
    log,
  }),
  z.strictObject({ type: z.literal('undoRequested'), actor: PlayerIdSchema, count: n, log }),
  z.strictObject({ type: z.literal('undoAnswered'), actor: PlayerIdSchema, accept: z.boolean(), log }),
  z.strictObject({ type: z.literal('undone'), actor: z.null(), by: PlayerIdSchema, count: n, log }),
  z.strictObject({
    type: z.literal('decisionRequested'),
    actor: z.null(),
    player: PlayerIdSchema,
    kind: DecisionKindSchema,
    log,
  }),
  z.strictObject({ type: z.literal('conceded'), actor: PlayerIdSchema, player: PlayerIdSchema, log }),
  z.strictObject({ type: z.literal('tierChanged'), actor: z.null(), from: TierSchema, to: TierSchema, log }),
  z.strictObject({ type: z.literal('gameEnded'), actor: z.null(), result: GameResultSchema, log }),
  // ── Rules events (tier 2+, engine `RulesEvent`; additive, protocol 1) ─────────────────────────────
  z.strictObject({
    type: z.literal('played'),
    actor: PlayerIdSchema,
    card: ref.nullable(),
    source: CardRefSchema,
    from: ZoneRefSchema.nullable(),
    item: ChainItemIdSchema.nullable(),
    ability: z.boolean(),
    log,
  }),
  z.strictObject({
    type: z.literal('resolved'),
    actor: PlayerIdSchema,
    source: ref.nullable(),
    card: CardRefSchema,
    item: ChainItemIdSchema.nullable(),
    what: z.enum(['card', 'ability', 'trigger']),
    log,
  }),
  z.strictObject({
    type: z.literal('damaged'),
    actor,
    object: ref,
    amount: n,
    source: ref.nullable(),
    combat: z.boolean(),
    log,
  }),
  z.strictObject({ type: z.literal('healed'), actor, objects: z.array(ref), log }),
  z.strictObject({ type: z.literal('killed'), actor, object: ref, result: ref.nullable(), from: ZoneRefSchema, log }),
  z.strictObject({
    type: z.literal('effectApplied'),
    actor,
    source: ref.nullable(),
    objects: z.array(ref),
    effect: PromptSchema,
    log,
  }),
  z.strictObject({
    type: z.literal('triggered'),
    actor: PlayerIdSchema,
    source: ref,
    reminder: text(64),
    label: text(200),
    log,
  }),
  z.strictObject({
    type: z.literal('combatStarted'),
    actor,
    battlefield: BattlefieldIdSchema,
    attacker: PlayerIdSchema,
    defender: PlayerIdSchema,
    log,
  }),
  z.strictObject({
    type: z.literal('showdownStarted'),
    actor,
    battlefield: BattlefieldIdSchema,
    focus: PlayerIdSchema,
    log,
  }),
  z.strictObject({
    type: z.literal('showdownEnded'),
    actor,
    battlefield: BattlefieldIdSchema,
    controller: PlayerIdSchema.nullable(),
    log,
  }),
  z.strictObject({
    type: z.literal('combatEnded'),
    actor,
    battlefield: BattlefieldIdSchema,
    winner: PlayerIdSchema.nullable(),
    result: z.enum(['attacker', 'defender', 'none']),
    log,
  }),
  z.strictObject({
    type: z.literal('scored'),
    actor: PlayerIdSchema,
    player: PlayerIdSchema,
    battlefield: BattlefieldIdSchema,
    method: z.enum(['conquer', 'hold']),
    gained: n,
    drew: z.boolean(),
    log,
  }),
  z.strictObject({ type: z.literal('countered'), actor, item: ChainItemIdSchema, source: ref.nullable(), log }),
  z.strictObject({ type: z.literal('burnedOut'), actor, player: PlayerIdSchema, to: PlayerIdSchema.nullable(), log }),
  z.strictObject({
    type: z.literal('bookkeeping'),
    actor: PlayerIdSchema,
    step: z.enum(['awaken', 'beginning', 'channel', 'draw', 'main', 'ending', 'expiration']),
    log,
  }),
  z.strictObject({ type: z.literal('reminderDismissed'), actor: PlayerIdSchema, source: ref.nullable(), log }),
  z.strictObject({ type: z.literal('cancelled'), actor: PlayerIdSchema, log }),
]);
